import { POIShape } from './POIShape';
import * as THREE from 'three';
import { AppSettingsData } from '../core/AppSettingsData';
import { SceneSettingsData } from '../core/SceneSettingsData';
import { ThreeGeomFactory } from './ThreeGeomFactory';
import { POI } from '../poi/POI';
import { Material } from '../materials/Material';
import { MaterialSet } from '../materials/MaterialFactory';

export class BoxShape extends POIShape {
    constructor(
        poi: POI,
        sceneSettings?: SceneSettingsData,
        appSettings?: AppSettingsData
    ) {
        super(poi, sceneSettings, appSettings);
        // The listeners for cornerRadius and aspectRatio are no longer needed
        // as the standard BoxGeometry does not have these properties.
    }

    protected override createGeometry(): THREE.BufferGeometry {
        // A box is a simple 1x1x1 cube. The final size is controlled by poidata.scale.
        const width = 1;
        const height = 1;
        const depth = 1;

        // Create standard box geometry (UVs will be adjusted after materials are created)
        return new THREE.BoxGeometry(width, height, depth);
    }
    
    /**
     * 🎯 Override buildMesh to add UV adjustment after materials are created
     */
    protected override buildMesh(): void {
        //console.log(`🔧 [BoxShape] buildMesh() called for ${this.poi.name}`);
        
        // Call parent implementation first to create materials and geometry
        super.buildMesh();
        
        // Now adjust UVs based on material aspect ratios
        if (this._geometry) {
            //console.log(`🎯 [BoxShape] Calling adjustBoxUVsForAspectRatio() immediately`);
            this.adjustBoxUVsForAspectRatio();
            
            // Also schedule delayed adjustment for when textures are loaded
            setTimeout(() => {
                //console.log(`⏰ [BoxShape] Delayed UV adjustment after 2 seconds`);
                this.adjustBoxUVsForAspectRatio();
            }, 2000);
        } else {
            console.warn(`❌ [BoxShape] No geometry available for UV adjustment`);
        }
    }
    
    /**
     * 🎯 Adjust BoxGeometry UVs to prevent texture stretching for non-square images
     */
    private adjustBoxUVsForAspectRatio(): void {
        //console.log(`🎯 [BoxShape] adjustBoxUVsForAspectRatio() called`);
        
        if (!this._geometry || !this._geometry.getAttribute('uv')) {
            console.warn(`❌ [BoxShape] Cannot adjust UVs - no geometry or UV attribute`);
            return;
        }

        // Get MaterialSet from the mesh (stored by parent buildMesh)
        const materialSet = (this as any)._materialSet as MaterialSet;
        if (!materialSet) {
            console.warn(`❌ [BoxShape] No MaterialSet found for UV adjustment`);
            return;
        }

        //console.log(`✅ [BoxShape] Found MaterialSet: front=${!!materialSet.front}, back=${!!materialSet.back}, sides=${!!materialSet.sides}`);

        const uris = this.poi.poidata.uri.value;
        const hasMultipleURIs = Array.isArray(uris) && uris.length > 1;
        
        //console.log(`🔧 [BoxShape] Adjusting BoxGeometry UVs for ${hasMultipleURIs ? 'multi-URI' : 'single-URI'} case`);
        //console.log(`📋 [BoxShape] URIs: ${JSON.stringify(uris)}`);

        // Get aspect ratios from materials
        //console.log(`📐 [BoxShape] Getting aspect ratios...`);
        const primaryAspect = this.getMaterialAspectRatio(materialSet.front);
        const secondaryAspect = materialSet.back ? this.getMaterialAspectRatio(materialSet.back) : 1.0;

        //console.log(`📐 [BoxShape] Final aspect ratios: primary=${primaryAspect.toFixed(3)}, secondary=${secondaryAspect.toFixed(3)}`);
        
        // Check if we need to adjust anything
        if (primaryAspect === 1.0 && secondaryAspect === 1.0) {
            //console.log(`⚪ [BoxShape] Both aspect ratios are 1.0 (square) - no UV adjustment needed`);
            return;
        }

        const uvArray = this._geometry.getAttribute('uv').array as Float32Array;
        const verticesPerFace = 4;
        const facesCount = 6;
        
        // BoxGeometry face order: Right, Left, Top, Bottom, Front, Back
        for (let faceIndex = 0; faceIndex < facesCount; faceIndex++) {
            const startVertex = faceIndex * verticesPerFace;
            let aspectRatio = 1.0;
            
            if (hasMultipleURIs) {
                // Multi-URI case: different aspect ratios for different faces
                if (faceIndex === 0 || faceIndex === 1) {
                    // Right, Left faces → use secondary material
                    aspectRatio = secondaryAspect;
                } else if (faceIndex === 2 || faceIndex === 3) {
                    // Top, Bottom faces → use solid material (skip UV adjustment)
                    continue;
                } else if (faceIndex === 4 || faceIndex === 5) {
                    // Front, Back faces → use primary material
                    aspectRatio = primaryAspect;
                }
            } else {
                // Single-URI case: same aspect ratio for all textured faces
                if (faceIndex === 2 || faceIndex === 3) {
                    // Skip solid faces if sides material exists
                    if (materialSet.sides) continue;
                }
                aspectRatio = primaryAspect;
            }
            
            //console.log(`🔧 [BoxShape] Face ${faceIndex}: aspect=${aspectRatio.toFixed(3)}`);
            
            // Adjust UVs for this face to preserve aspect ratio
            for (let vertexIndex = 0; vertexIndex < verticesPerFace; vertexIndex++) {
                const uvIndex = (startVertex + vertexIndex) * 2;
                
                if (uvIndex + 1 < uvArray.length) {
                    let u = uvArray[uvIndex];
                    let v = uvArray[uvIndex + 1];
                    
                    // Center and scale based on aspect ratio (crop instead of stretch)
                    if (aspectRatio > 1.0) {
                        // Wide image: crop left/right, center horizontally
                        const scale = 1.0 / aspectRatio;
                        const offset = (1 - scale) / 2;
                        u = offset + u * scale;
                    } else if (aspectRatio < 1.0) {
                        // Tall image: crop top/bottom, center vertically
                        const scale = aspectRatio;
                        const offset = (1 - scale) / 2;
                        v = offset + v * scale;
                    }
                    
                    // Clamp to [0,1] range
                    uvArray[uvIndex] = Math.max(0, Math.min(1, u));
                    uvArray[uvIndex + 1] = Math.max(0, Math.min(1, v));
                }
            }
        }
        
        // Mark UV attribute as needing update
        this._geometry.getAttribute('uv').needsUpdate = true;
        //console.log(`✅ [BoxShape] BoxGeometry UVs adjusted for aspect ratio preservation`);
        //console.log(`📊 [BoxShape] UV Array modified: ${uvArray.length} floats, needsUpdate=true`);
    }

    /**
     * 🎯 BOX-SPECIFIC: Override material group assignment for 6-face BoxGeometry
     */
    protected override assignMaterialGroupsToGeometry(materialSet: MaterialSet): void {
        if (!this._geometry || !this._geometry.groups || this._geometry.groups.length !== 6) {
            // Fall back to parent implementation for non-BoxGeometry
            super.assignMaterialGroupsToGeometry(materialSet);
            return;
        }

        //console.log(`🔧 [BoxShape] Assigning materials to 6-face BoxGeometry`);
        
        const uris = this.poi.poidata.uri.value;
        const hasMultipleURIs = Array.isArray(uris) && uris.length > 1;
        
        if (hasMultipleURIs && materialSet.back) {
            // BOX with multiple URIs: Front/Back = primary, Left/Right = secondary, Top/Bottom = solid
            //console.log(`🎯 [BoxShape] Multi-URI: Front/Back=primary, Left/Right=secondary, Top/Bottom=solid`);
            this._geometry.groups[0].materialIndex = 1; // Right (+X) = secondary
            this._geometry.groups[1].materialIndex = 1; // Left (-X) = secondary
            this._geometry.groups[2].materialIndex = 2; // Top (+Y) = solid
            this._geometry.groups[3].materialIndex = 2; // Bottom (-Y) = solid
            this._geometry.groups[4].materialIndex = 0; // Front (+Z) = primary
            this._geometry.groups[5].materialIndex = 0; // Back (-Z) = primary
        } else if (materialSet.sides) {
            // BOX with sides material: Front = texture, others = solid
            const useBacksideSideMaterial = this.poi.poidata.useBacksideSideMaterial?.value ?? false;
            //console.log(`🎯 [BoxShape] Single-URI + sides: Front=texture, others=${useBacksideSideMaterial ? 'solid' : 'texture'}`);
            
            if (useBacksideSideMaterial) {
                this._geometry.groups[0].materialIndex = 1; // Right = solid
                this._geometry.groups[1].materialIndex = 1; // Left = solid
                this._geometry.groups[2].materialIndex = 1; // Top = solid
                this._geometry.groups[3].materialIndex = 1; // Bottom = solid
                this._geometry.groups[4].materialIndex = 0; // Front = texture
                this._geometry.groups[5].materialIndex = 1; // Back = solid
            } else {
                // All faces use the same texture
                for (let i = 0; i < 6; i++) {
                    this._geometry.groups[i].materialIndex = 0;
                }
            }
        } else {
            // Simple case: all faces use same material
            //console.log(`🎯 [BoxShape] Single material for all faces`);
            for (let i = 0; i < 6; i++) {
                this._geometry.groups[i].materialIndex = 0;
            }
        }
        
        // Debug final assignments
        const assignments = this._geometry.groups.map((group, i) => `face[${i}]=${group.materialIndex}`).join(', ');
        //console.log(`🎯 [BoxShape] Final assignments: ${assignments}`);
    }

    /**
     * 🎯 Get aspect ratio from material (width/height)
     */
    private getMaterialAspectRatio(material: Material): number {
        if (!material) {
            //console.log(`🔍 [BoxShape] getMaterialAspectRatio: no material - returning 1.0`);
            return 1.0;
        }
        
        //console.log(`🔍 [BoxShape] getMaterialAspectRatio for ${material.constructor.name}`);
        
        // Try to get aspect ratio from material property
        if ('aspectRatio' in material && (material as any).aspectRatio?.value !== undefined) {
            const aspectRatio = (material as any).aspectRatio.value;
            //console.log(`📐 [BoxShape] Found aspectRatio property: ${aspectRatio}`);
            return aspectRatio;
        }
        
        // Fallback: try to get from texture if loaded
        const threeMaterial = material.getThreeMaterial();
        if (threeMaterial && (threeMaterial as any).map && (threeMaterial as any).map.image) {
            const image = (threeMaterial as any).map.image;
            if (image.width && image.height) {
                const aspectRatio = image.width / image.height;
                //console.log(`📐 [BoxShape] Calculated from texture: ${aspectRatio} (${image.width}x${image.height})`);
                return aspectRatio;
            }
        }
        
        //console.log(`📐 [BoxShape] No aspect ratio found - returning 1.0 (square)`);
        return 1.0; // Default to square
    }
}